import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Modal,
  Button,
  Chip,
  Divider,
  Stack,
  useTheme,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import UpdateIcon from "@mui/icons-material/Update";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { getUserVersionHistory } from "../../services/user.service";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "800px",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: "12px",
  outline: "none",
  border: "1px solid #e0e0e0",
};

const changeItemStyle = {
  p: 2,
  mb: 2,
  bgcolor: "background.default",
  borderRadius: "8px",
  borderLeft: "4px solid",
};

// Helper function to render object values
const renderObjectValue = (value) => {
  if (typeof value === "object" && value !== null) {
    return (
      <Box
        sx={{
          mt: 1,
          p: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
        }}
      >
        {Object.entries(value).map(([key, val]) => (
          <Box key={key} sx={{ mb: 1 }}>
            <Typography variant="caption" color="#9e9e9e">
              {key.replace(/([A-Z])/g, " $1").trim()}:
            </Typography>
            <Typography sx={{ wordBreak: "break-word" }}>
              {val === null || val === "" ? <em>Empty</em> : String(val)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return String(value) || <em>Empty</em>;
};

function VersionHistoryModal({ userId }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const theme = useTheme();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    if (open) {
      getUserVersionHistory(userId).then((res) => {
        if (res?.result) setHistory(res.result);
      });
    }
  }, [open, userId]);

  return (
    <>
      <Button
        startIcon={<HistoryIcon />}
        onClick={handleOpen}
        variant="outlined"
        sx={{
          mb: 2,
          color: "#5d5d5d",
          borderColor: "#e0e0e0",
          "&:hover": {
            borderColor: "#bdbdbd",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        Version History
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HistoryIcon sx={{ color: "#5e35b1" }} fontSize="medium" />
              <Typography variant="h5" component="h2" sx={{ color: "#424242" }}>
                Version History
              </Typography>
              <Chip
                label={`${history.length} changes`}
                size="small"
                sx={{
                  backgroundColor: "#ede7f6",
                  color: "#5e35b1",
                  border: "none",
                }}
              />
            </Stack>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "#757575",
                "&:hover": {
                  color: "#424242",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {history.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "#f5f5f5",
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="#757575">
                No version history available
              </Typography>
            </Box>
          ) : (
            history.map((entry, index) => (
              <Accordion
                key={index}
                expanded={expanded === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                sx={{
                  mb: 2,
                  borderRadius: "8px !important",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#616161" }} />}
                  sx={{
                    bgcolor:
                      expanded === `panel${index}`
                        ? "#f5f5f5"
                        : "background.paper",
                    borderRadius: "8px",
                    "&.Mui-expanded": { borderRadius: "8px 8px 0 0" },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#d1c4e9",
                        borderRadius: "50%",
                        color: "#5e35b1",
                        fontWeight: 500,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={500}
                        color="#424242"
                      >
                        {moment(entry.changedAt).format("DD MMM YYYY, hh:mm A")}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon
                          fontSize="small"
                          sx={{ color: "#9e9e9e" }}
                        />
                        <Typography variant="body2" color="#757575">
                          {entry.changedBy?.name || "Unknown user"}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Divider sx={{ mb: 2, borderColor: "#eeeeee" }} />

                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      color: "#616161",
                    }}
                  >
                    <UpdateIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "#616161" }}
                    />
                    {entry.changes.length} field
                    {entry.changes.length > 1 ? "s" : ""} changed
                  </Typography>

                  {entry.changes.map((change, i) => (
                    <Box
                      key={i}
                      sx={{
                        ...changeItemStyle,
                        borderLeftColor: "#7cb342",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          textTransform: "capitalize",
                          mb: 1,
                          color: "#424242",
                        }}
                      >
                        {change.field.replace(/([A-Z])/g, " $1").trim()}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="#9e9e9e">
                            Previous value
                          </Typography>
                          <Box
                            sx={{
                              p: 1,
                              bgcolor: "#ffebee",
                              borderRadius: 1,
                              mt: 0.5,
                              wordBreak: "break-word",
                              color: "#c62828",
                            }}
                          >
                            {renderObjectValue(change.oldValue)}
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="#9e9e9e">
                            New value
                          </Typography>
                          <Box
                            sx={{
                              p: 1,
                              bgcolor: "#e8f5e9",
                              borderRadius: 1,
                              mt: 0.5,
                              wordBreak: "break-word",
                              color: "#2e7d32",
                            }}
                          >
                            {renderObjectValue(change.newValue)}
                          </Box>
                        </Box>
                      </Stack>
                    </Box>
                  ))}

                  {entry.thirdPartyUpdateStatus && (
                    <Box
                      sx={{
                        ...changeItemStyle,
                        borderLeftColor: "#0288d1",
                        mt: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <InfoIcon sx={{ color: "#0288d1" }} fontSize="small" />
                        <Typography variant="subtitle2" color="#616161">
                          System Status
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="#9e9e9e">
                            Update Status
                          </Typography>
                          <Typography
                            sx={{
                              p: 1,
                              bgcolor: "#e3f2fd",
                              borderRadius: 1,
                              mt: 0.5,
                              color: "#01579b",
                            }}
                          >
                            {entry.thirdPartyUpdateStatus}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="#9e9e9e">
                            Response Message
                          </Typography>
                          <Typography
                            sx={{
                              p: 1,
                              bgcolor: "#e3f2fd",
                              borderRadius: 1,
                              mt: 0.5,
                              wordBreak: "break-word",
                              color: "#01579b",
                            }}
                          >
                            {entry.thirdPartyResponseMessage || (
                              <em>No message</em>
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Modal>
    </>
  );
}

export default VersionHistoryModal;
